import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { checkOverflow, getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import { maskText, stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-text.scss';
import { type IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';
import IdsButton from '../ids-button/ids-button';
import { isValidInteger } from '../../utils/ids-number-utils/ids-number-utils';

const fontWeightClasses = ['bold', 'semi-bold', 'lighter'] as const;
type FontWeightClasses = typeof fontWeightClasses[number];

// Mask types
export type MaskType = 'button-mask' | 'masked-only' | 'icon-mask' | null;

type MaskButtonLabel = { label: string, icon: string };

// These types will have a CSS style class appended to them
const typesCssClasses = ['label', 'legend', 'span'];

// Text alignments
const TEXT_ALIGNMENTS = ['start', 'end', 'center', 'justify'];

// Statuses
const STATUSES = ['base', 'error', 'info', 'success', 'warning'];

export type WhiteSpaceTypes = 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line' | 'break-spaces' | null;

const whiteSpaceClasses = ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'];

export type WordBreakTypes = 'normal' | 'break-all' | 'keep-all' | 'break-word' | null;

const wordBreakClasses = ['normal', 'break-all', 'keep-all', 'break-word'];

const fontSizes: Array<object> = [
  { 10: '10px' },
  { 12: '12px' },
  { 14: '14px' },
  { 16: '16px' },
  { 20: '20px' },
  { 24: '24px' },
  { 28: '28px' },
  { 32: '32px' },
  { 40: '40px' },
  { 48: '48px' },
  { 60: '60px' },
  { 72: '72px' }
];

const Base = IdsColorVariantMixin(
  IdsLocaleMixin(
    IdsTooltipMixin(
      IdsEventsMixin(
        IdsElement
      )
    )
  )
);

/**
 * IDS Text Component
 * @type {IdsText}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsEventsMixin
 * @part text - the text element
 */
@customElement('ids-text')
@scss(styles)
export default class IdsText extends Base {
  rootNode?: any;

  #originalText = '';

  #parentWrapper: IdsElement | null = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.#setMaskType(this?.maskType);
    if (this.color) this.color = this.getAttribute('color') as IdsColorValue | 'unset' | 'muted';
    if (this.lineClamp) this.lineClamp = this.lineClamp;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.AUDIBLE,
      attributes.COLOR,
      attributes.DATA,
      attributes.DISABLED,
      attributes.DISPLAY,
      attributes.ERROR,
      attributes.FONT_SIZE,
      attributes.FONT_WEIGHT,
      attributes.LABEL,
      attributes.LABEL_WIDTH,
      attributes.LABEL_WRAP,
      attributes.LINE_CLAMP,
      attributes.MAX_WIDTH,
      attributes.OVERFLOW,
      attributes.STATUS,
      attributes.MASK_TYPE,
      attributes.MASKED,
      attributes.TEXT_MASK_CHARACTER,
      attributes.TEXT_ALIGN,
      attributes.TRANSLATE_TEXT,
      attributes.TYPE,
      attributes.WORD_WRAP,
      attributes.WHITE_SPACE,
      attributes.WORD_BREAK,
    ];
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const tag = this.type || 'span';
    const fontWeight = this.fontWeight;

    let classList = 'ids-text';
    classList += this.status ? ` ${this.statusClass()}` : '';
    classList += this.textAlign ? ` ${this.textAlignClass()}` : '';
    classList += (this.overflow === 'ellipsis') ? ' ellipsis' : '';
    classList += (this.audible) ? ' audible' : '';
    classList += (this.label) ? ' label' : '';
    classList += this.fontSize ? ` ids-text-${this.fontSize}` : '';
    classList += this.whiteSpace ? ` ${this.whiteSpace}` : '';
    classList += this.wordBreak ? ` word-break-${this.wordBreak}` : '';
    classList += (fontWeight === 'bold' || fontWeight === 'lighter' || fontWeight === 'semi-bold')
      ? ` ${this.fontWeight}` : '';

    let template = `<${tag}
      class="${classList}"
      part="text"
    ><slot></slot>
    </${tag}>`;

    this.rootNode = (this.getRootNode() as any).host;
    if (this.rootNode && this.rootNode.name === 'ids-tab' && (this.rootNode.getAttribute('min-width') || this.rootNode.getAttribute('max-width'))) {
      template = `<${tag}
        class="${classList}"
        part="text"
      ><div class='has-tab-width'><slot></slot></div>
      </${tag}>`;
    }

    return template;
  }

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate'];

  /**
   * Handle internal events
   * @private
   */
  #attachEventHandlers() {
    if (this.translateText) {
      this.onLanguageChange = async () => {
        await this.#translateAsync();
      };

      if (!this.getAttribute('translation-key')) {
        this.setAttribute('translation-key', this.textContent ?? '');
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.#translateAsync();
    }

    this.onLocaleChange = () => {
      this.#refreshContent();
    };
  }

  /**
   * Set the font size of the text with a class.
   * @param {string | number | null} value The font size in the font scheme
   * i.e. 10, 12, 16 etc or semantic tokens like 'sm', 'md', etc.
   */
  set fontSize(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.FONT_SIZE, `${value}`);
    } else {
      this.removeAttribute(attributes.FONT_SIZE);
    }

    if (this.container) {
      fontSizes?.forEach((size) => {
        this.container?.classList.remove(`ids-text-${Object.keys(size)[0]}`);
      });

      if (value) {
        // Map semantic tokens to numeric values
        const tokenToNumeric: Record<string, string> = {
          '3xs': '10',
          '2xs': '12',
          xs: '14',
          sm: '16',
          md: '20',
          lg: '24',
          xl: '28',
          '2xl': '32',
          '3xl': '40',
          '4xl': '48',
          '5xl': '60',
          '6xl': '72'
        };

        // Use numeric value if it's a token, otherwise use the value directly
        const numericValue = tokenToNumeric[`${value}`] || value;
        this.container.classList.add(`ids-text-${numericValue}`);
      }
    }
  }

  get fontSize(): string | null { return this.getAttribute(attributes.FONT_SIZE); }

  /**
   * Sets the number of characters to reveal in masked text
   * @param {number | null} value The number of characters to reveal from the beginning of the text
   */
  set textMaskCharacter(value: number | null) {
    if (!value || !isValidInteger(value)) {
      this.removeAttribute(attributes.TEXT_MASK_CHARACTER);
      return;
    }

    const valueToSet = value > 4 ? 4 : value;
    this.setAttribute(attributes.TEXT_MASK_CHARACTER, `${valueToSet}`);
    this.#processMaskText();
  }

  get textMaskCharacter(): number {
    return this.getAttribute(attributes.TEXT_MASK_CHARACTER) ? Number(this.getAttribute(attributes.TEXT_MASK_CHARACTER)) : 0;
  }

  /**
   * Sets the mask type for the text component
   * @param {MaskType} value The mask type to apply ('masked-only', 'button-mask', 'icon-mask', or null)
   */
  set maskType(value: MaskType) {
    if (value) {
      this.setAttribute(attributes.MASK_TYPE, `${value}`);
      this.#setMaskType(value);
    } else {
      this.removeAttribute(attributes.MASK_TYPE);
      this.#setMaskType(value);
    }
  }

  /**
   * Gets the current mask type
   * @returns {MaskType} The current mask type
   */
  get maskType(): MaskType { return this.getAttribute(attributes.MASK_TYPE) as MaskType; }

  /**
   * Sets whether the text is masked or not
   * @param {boolean | string} value True to mask the text, false to show original text
   */
  set masked(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.MASKED, '');
      this.#parentWrapper?.setAttribute(attributes.MASKED, '');
    } else {
      this.removeAttribute(attributes.MASKED);
      this.#parentWrapper?.removeAttribute(attributes.MASKED);
    }

    this.#processMaskText();
  }

  /**
   * Gets whether the text is currently masked
   * @returns {boolean | string} True if the text is masked
   */
  get masked(): boolean | string {
    return this.hasAttribute(attributes.MASKED);
  }

  /**
   * Sets the word-break behavior for the text
   * @param {WordBreakTypes} value The word-break value ('normal', 'break-all', 'keep-all', 'break-word', or null)
   */
  set wordBreak(value: WordBreakTypes) {
    if (value && wordBreakClasses.includes(value)) {
      this.setAttribute(attributes.WORD_BREAK, value);
    } else {
      this.removeAttribute(attributes.WORD_BREAK);
    }

    if (this.container) {
      wordBreakClasses.forEach((cls) => {
        this.container?.classList.remove(`word-break-${cls}`);
      });

      if (value && wordBreakClasses.includes(value)) {
        this.container.classList.add(`word-break-${value}`);
      }
    }
  }

  /**
   * Gets the current word-break value
   * @returns {WordBreakTypes} The current word-break value
   */
  get wordBreak(): WordBreakTypes {
    return this.getAttribute(attributes.WORD_BREAK) as WordBreakTypes;
  }

  get maskBtn(): IdsButton | null {
    return this.shadowRoot?.querySelector('.mask-btn') || null;
  }

  /**
   * Adjust font weight; can be either "bold" or "lighter"
   * @param {string | null} value (if bold)
   */
  set fontWeight(value: 'lighter' | 'bold' | 'semi-bold' | null) {
    this.container?.classList.remove(...fontWeightClasses);

    if (value && fontWeightClasses.includes(value)) {
      this.setAttribute(attributes.FONT_WEIGHT, value);
      this.container?.classList.add(value);
    } else {
      this.removeAttribute(attributes.FONT_WEIGHT);
    }
  }

  get fontWeight(): FontWeightClasses | null {
    return this.getAttribute(attributes.FONT_WEIGHT) as FontWeightClasses | null;
  }

  /**
   * Set the type of HTML element is (h1-h6, p, span (default))
   * @param {string | null} value  The type of element
   */
  set type(value: string | null) {
    if (value) {
      this.setAttribute(attributes.TYPE, value);
    } else {
      this.removeAttribute(attributes.TYPE);
    }

    this.render(true);
    this.#setMaskType(this?.maskType);
    this.#setTypeClass(value ?? '');
  }

  get type(): string | null { return this.getAttribute(attributes.TYPE); }

  /**
   * Set the how to address the white-space in the element.
   * @param {WhiteSpaceTypes} value The white-space of element
   */
  set whiteSpace(value: WhiteSpaceTypes) {
    if (value) {
      this.setAttribute(attributes.WHITE_SPACE, value);
    } else {
      this.removeAttribute(attributes.WHITE_SPACE);
    }
    this.#setWhiteSpaceClasses(value);
  }

  get whiteSpace(): WhiteSpaceTypes | null {
    return this.getAttribute(attributes.WHITE_SPACE) as WhiteSpaceTypes | null;
  }

  /**
   * Sets a CSS Class on the container element for some Text types
   * @param {string} value the class type to check/add
   * @returns {void}
   */
  #setTypeClass(value: string): void {
    this.container?.classList.remove(...typesCssClasses);
    if (typesCssClasses.includes(value)) {
      this.container?.classList.add(value);
    }
  }

  /**
   * Sets up the mask type functionality based on the provided mask type
   * @private
   * @param {MaskType} maskType The type of mask to apply
   */
  #setMaskType(maskType: MaskType): void {
    if (!maskType) {
      return;
    }

    if (maskType === 'masked-only') {
      this.masked = true;
      this.offEvent('click.text-mask-btn');
      this.maskBtn?.remove();
    } else if (maskType === 'button-mask' || maskType === 'icon-mask') {
      if (this.maskBtn) {
        this.offEvent('click.text-mask-btn');
        this.maskBtn?.remove();
      }
      const maskBtnLabels = this.getMaskBtnLabel();
      const maskButton: any = document.createElement('ids-button');
      maskButton.setAttribute(attributes.TEXT, maskType === 'icon-mask' ? '' : maskBtnLabels.label);
      maskButton.setAttribute(attributes.ICON, maskBtnLabels.icon);
      maskButton.classList.add('mask-btn');
      maskButton.noPadding = true;
      this.shadowRoot?.appendChild(maskButton);

      this.offEvent('click.text-mask-btn', this.maskBtn);
      this.onEvent('click.text-mask-btn', this.maskBtn, () => {
        const wrapper = getClosest(this, 'ids-data-label');
        if (this.masked) {
          this.removeAttribute(attributes.MASKED);
          wrapper?.removeAttribute(attributes.MASKED);
        } else {
          this.setAttribute(attributes.MASKED, '');
          wrapper?.setAttribute(attributes.MASKED, '');
        }
      });
    } else {
      this.masked = false;
      this.offEvent('click.text-mask-btn');
      this.maskBtn?.remove();
    }
  }

  /**
   * Processes the text content based on mask settings
   * @private
   */
  #processMaskText() {
    if (!this?.maskType) {
      return;
    }

    const toMask = this.masked || this.maskType === 'masked-only';

    // Get text from slot if textContent is empty
    const slot = this.shadowRoot?.querySelector('slot');
    const slotText = slot?.assignedNodes({ flatten: true })
      .map((node) => node.textContent || '')
      .join('') || '';
    const currentText = this.textContent?.trim() || slotText.trim();

    const maskBtn = this.maskBtn;
    if (toMask) {
      this.#originalText = this.#originalText || currentText;
      const maskedContent = maskText(currentText, this.textMaskCharacter);
      this.textContent = maskedContent;
    } else {
      this.textContent = this.#originalText;
    }

    if (maskBtn) {
      const label = this.getMaskBtnLabel();
      maskBtn.setAttribute(attributes.TEXT, this.maskType === 'icon-mask' ? '' : label.label);
      maskBtn.setAttribute(attributes.ICON, label.icon);
    }
  }

  /**
   * Gets the appropriate label and icon for the mask button based on current mask state
   * @returns {MaskButtonLabel} Object containing label and icon properties
   */
  getMaskBtnLabel(): MaskButtonLabel {
    const state = this.masked ? 'masked' : 'unmasked';

    return {
      masked: {
        label: this.localeAPI?.translate('Show'),
        icon: 'show-item'
      },
      unmasked: {
        label: this.localeAPI?.translate('Hide'),
        icon: 'hide-item'
      }
    }[state];
  }

  /**
   * Sets a CSS Class on the container element for white space
   * @param {WhiteSpaceTypes} value the class type to check/add
   * @returns {void}
   */
  #setWhiteSpaceClasses(value: WhiteSpaceTypes): void {
    this.container?.classList.remove(...whiteSpaceClasses);
    if (value && whiteSpaceClasses.includes(value)) {
      this.container?.classList.add(value);
    }
  }

  /**
   * If set to "unset", color can be controlled by parent container
   * If set to "muted" will apply as disabled color
   * @param {IdsColorValue} value  "unset" or undefined/null
   */
  set color(value: IdsColorValue | 'unset' | 'muted') {
    const unsetClass = 'ids-text-color-unset';
    const mutedClass = 'ids-text-color-muted';
    this.container?.classList.remove(unsetClass, mutedClass);

    if (typeof value === 'string' && value !== '') {
      this.setAttribute(attributes.COLOR, value);
      if (value === 'unset') {
        this.container?.classList.add(unsetClass);
        this.container?.style.removeProperty('color');
      } else if (value === 'muted') {
        this.setAttribute(attributes.COLOR, value);
        this.container?.classList.add(mutedClass);
      } else this.container?.style.setProperty('color', `var(--ids-color-${value})`);
      return;
    }
    this.removeAttribute(attributes.COLOR);
  }

  get color(): IdsColorValue | 'unset' | 'muted' {
    return this.getAttribute(attributes.COLOR) as IdsColorValue;
  }

  /**
   * Set audible as screen reader only text
   * @param {boolean | string} value The audible value
   */
  set audible(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.AUDIBLE, value.toString());
      if (this.container && !this.container.classList.contains(attributes.AUDIBLE)) {
        this.container.classList.add('audible');
      }
    } else {
      this.removeAttribute(attributes.AUDIBLE);
      this.container?.classList.remove('audible');
    }
  }

  get audible(): boolean | string { return stringToBool(this.getAttribute(attributes.AUDIBLE)); }

  /**
   * Get the visible text content, excluding any child ids-text elements with audible="true"
   * @returns {string} the visible text content
   */
  get visibleTextContent(): string {
    const clone = this.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('ids-text[audible="true"]').forEach((el) => el.remove());
    return clone.textContent?.trim() || '';
  }

  /**
   * Set the text to disabled color.
   * @param {boolean | string} value True if disabled
   */
  set disabled(value: boolean | string) {
    if (!this.shadowRoot) return;

    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot');
    if (!slot) return;
    const slottedElements = slot.assignedElements();
    const isDisabled = stringToBool(value);

    slottedElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.toggleAttribute(attributes.DISABLED, isDisabled);
      }
    });
    this.toggleAttribute(attributes.DISABLED, isDisabled);
  }

  get disabled(): boolean | string { return stringToBool(this.getAttribute(attributes.DISABLED)); }

  /**
   * Set the text to error color.
   * @param {boolean} value True if error text
   */
  set error(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      if (this.container) this.container.classList.add('error');
      this.setAttribute(attributes.ERROR, val.toString());
      return;
    }
    this.removeAttribute(attributes.ERROR);
    if (this.container) this.container.classList.remove('error');
  }

  get error(): boolean { return stringToBool(this.getAttribute(attributes.ERROR)); }

  /**
   * Set the text to label color.
   * @param {boolean} value True if label text
   */
  set label(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.LABEL, val.toString());
      this.container?.classList.add('label');
      return;
    }
    this.removeAttribute(attributes.LABEL);
    if (this.container) this.container.classList.remove('label');
  }

  get label(): boolean { return stringToBool(this.getAttribute(attributes.LABEL)); }

  /**
   * Set the text to data color.
   * @param {boolean | string} value True if data text
   */
  set data(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.container?.classList.add('data');
      this.setAttribute(attributes.DATA, val.toString());
      return;
    }
    this.removeAttribute(attributes.DATA);
    this.container?.classList.remove('data');
  }

  get data(): boolean | string { return stringToBool(this.getAttribute(attributes.DATA)); }

  /**
   * Set the maxWidth of the text (used for ellipsis)
   * @param {string | null} value The value of the max-width
   */
  set maxWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_WIDTH, value);
      this.container?.style.setProperty('max-width', `${parseInt(value)}px`, 'important');
    } else {
      this.removeAttribute(attributes.MAX_WIDTH);
      this.container?.style.removeProperty('max-width');
    }
  }

  get maxWidth(): string | null { return this.getAttribute(attributes.MAX_WIDTH); }

  /**
   * Truncates text at a specific number of lines.
   * @param {string | number | null} value The number of lines
   */
  set lineClamp(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.LINE_CLAMP, `${value}`);
      this.container?.style.setProperty('-webkit-line-clamp', `${value}`);
      this.container?.classList.add('has-line-clamp');
      this.container?.classList.toggle('line-clamp-one', Number(value) === 1);
    } else {
      this.removeAttribute(attributes.LINE_CLAMP);
      this.container?.style.removeProperty('-webkit-line-clamp');
      this.container?.classList.remove('has-line-clamp');
      this.container?.classList.remove('line-clamp-one');
    }
  }

  get lineClamp(): string | null { return this.getAttribute(attributes.LINE_CLAMP); }

  /**
   * Set how content overflows; can specify 'ellipsis', or undefined or 'none'
   * @param {string | null} value how content is overflow
   */
  set overflow(value: string | null) {
    const isEllipsis = value === 'ellipsis';

    if (isEllipsis) {
      this.container?.classList.add('ellipsis');
      this.setAttribute(attributes.OVERFLOW, 'ellipsis');
    } else {
      this.container?.classList.remove('ellipsis');
      this.removeAttribute(attributes.OVERFLOW);
    }
  }

  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Set the given text align
   * @param {string} value The value
   */
  set textAlign(value: string) {
    this.container?.classList.remove(...this.textAlignClass(true));

    if (TEXT_ALIGNMENTS.includes(value)) {
      this.setAttribute(attributes.TEXT_ALIGN, value);
      this.container?.classList.add(this.textAlignClass() as string);
    } else {
      this.removeAttribute(attributes.TEXT_ALIGN);
    }
  }

  get textAlign(): string {
    return this.getAttribute(attributes.TEXT_ALIGN) ?? '';
  }

  /**
   * Get text-align class name/s with prefixed value
   * @param {boolean|undefined} isAll If true, will return all classes as array
   * @returns {string|Array<string>} The class name with prefix
   */
  textAlignClass(isAll?: boolean): string | Array<string> {
    const prefixed = (v: string) => `text-align-${v}`;
    return isAll ? TEXT_ALIGNMENTS.map((v) => prefixed(v)) : prefixed(this.textAlign);
  }

  /**
   * Set the given status
   * @param {string} value The value
   */
  set status(value: string | null) {
    this.container?.classList.remove(...this.statusClass(true));

    if (STATUSES.includes(value ?? '')) {
      this.setAttribute(attributes.STATUS, value ?? '');
      this.container?.classList.add(this.statusClass() as string);
    } else {
      this.removeAttribute(attributes.STATUS);
    }
  }

  get status(): string | null {
    return this.getAttribute(attributes.STATUS);
  }

  /**
   * Get status class name/s with prefixed value
   * @param {boolean|undefined} isAll If true, will return all classes as array
   * @returns {string|Array<string>} The class name with prefix
   */
  statusClass(isAll?: boolean): string | Array<string> {
    const prefixed = (v: string) => `status-${v}`;
    return isAll ? STATUSES.map((v) => prefixed(v)) : prefixed(this.status ?? '');
  }

  /**
   * If set to true the value in the text will be considered a key and
   * sent to the translation function
   * @param {boolean} value If true translate this value
   */
  set translateText(value: boolean) {
    const val = stringToBool(value);
    if (val && !this.getAttribute('translation-key')) {
      this.setAttribute(attributes.TRANSLATE_TEXT, val.toString());
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.#translateAsync();
    }
  }

  get translateText(): boolean {
    return stringToBool(this.getAttribute(attributes.TRANSLATE_TEXT));
  }

  /**
   * Translate the contents asyncronously
   * @private
   */
  async #translateAsync() {
    const translationKey = this.getAttribute('translation-key');
    const translationParams = this.getAttribute('translation-params');

    if (!this.localeAPI || !translationKey) {
      return;
    }

    if (translationParams) {
      const params = translationParams.split(',').map((p) => p.trim());
      let translatedText = this.localeAPI.translate(translationKey);
      params.forEach((param, index) => {
        translatedText = translatedText.replace(`{${index}}`, param);
      });
      this.textContent = translatedText;
    } else {
      this.textContent = this.localeAPI.translate(translationKey);
    }
  }

  /**
   * Set the text decoration style
   * @param {string | null} value The text decoration value ('dotted-underline' or null)
   */
  set textDecoration(value: string | null) {
    this.container?.classList.remove('text-decoration');
    if (value === 'dotted-underline') {
      this.setAttribute(attributes.TEXT_DECORATION, value);
      this.container?.classList.add('text-decoration');
    } else {
      this.removeAttribute(attributes.TEXT_DECORATION);
    }
    this.#updateDottedUnderlineState();
  }

  get textDecoration(): string | null {
    return this.getAttribute(attributes.TEXT_DECORATION);
  }

  /**
   * Updates tabindex state based on text-decoration and tooltip combination
   * @private
   */
  #updateDottedUnderlineState(): void {
    const hasDottedUnderline = this.textDecoration === 'dotted-underline';
    const hasTooltip = this.tooltip && this.tooltip !== 'false';
    const isDisabled = this.hasAttribute(attributes.DISABLED);

    if (hasDottedUnderline && hasTooltip && !isDisabled) {
      this.setAttribute(attributes.TABINDEX, '0');
    } else {
      this.removeAttribute(attributes.TABINDEX);
    }
  }

  /**
   * Set the tooltip value, syncing dotted underline state
   * @param {string} value The tooltip value
   */
  set tooltip(value: string) {
    super.tooltip = value;
    this.#updateDottedUnderlineState();
  }

  get tooltip(): string {
    return super.tooltip;
  }

  canTooltipShow(): boolean {
    if (this.disabled) return false;

    // Check if it has line-clamp and it overflows
    if (this.lineClamp) {
      return checkOverflow(this.container) || checkOverflow(this) || false;
    }

    // If tooltip is not set or explicitly disabled, don't show
    if (!this.tooltip || this.tooltip === 'false') {
      return false;
    }

    // Check for label-wrap ellipsis variants
    const labelWrap = this.getAttribute(attributes.LABEL_WRAP);
    if (labelWrap && ['ellipsis', 'ellipsis-no-stretch'].includes(labelWrap)) {
      return checkOverflow(this.container) || checkOverflow(this) || false;
    }

    // If tooltip is set but overflow is not 'ellipsis', always show tooltip
    if (this.overflow !== 'ellipsis') {
      return true;
    }

    // For ellipsis overflow, check if the element is actually overflowing
    if (this.overflow === 'ellipsis') {
      // Special case for popup root nodes
      if (this?.classList.contains('ids-menu-item-text')) {
        return checkOverflow(this.container, false);
      }

      // Special case for tabs
      if (this.parentElement?.classList.contains('ids-tab')) {
        return ((checkOverflow(this.container, false) || checkOverflow(this.parentElement))) || false;
      }

      // Check if the container or parent element is overflowing
      if (this.container) {
        return ((checkOverflow(this.container) || checkOverflow(this.parentElement))) || false;
      }
    }

    return false;
  }

  /**
   * Set the word wrap property to enable text wrapping
   * @param {string} value The word wrap value ('break-word' or null)
   */
  set wordWrap(value: string | null) {
    if (value) {
      this.setAttribute(attributes.WORD_WRAP, value);
    } else {
      this.removeAttribute(attributes.WORD_WRAP);
    }
  }

  /**
   * Get the current word wrap setting
   * @returns {boolean} The current word wrap value
   */
  get wordWrap(): boolean {
    return this.hasAttribute(attributes.WORD_WRAP);
  }

  /**
   * Set the translation key for localization
   * @param {string | null} value The translation key
   */
  set translationKey(value: string | null) {
    if (value) {
      this.setAttribute(attributes.TRANSLATION_KEY, value);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.#translateAsync();
    } else {
      this.removeAttribute(attributes.TRANSLATION_KEY);
    }
  }

  /**
   * Get the current translation key
   * @returns {string | null} The translation key
   */
  get translationKey(): string | null {
    return this.getAttribute(attributes.TRANSLATION_KEY);
  }

  /**
   * Set the translation parameters for parameterized translations
   * @param {string | null} value The translation parameters (comma-separated)
   */
  set translationParams(value: string | null) {
    if (value) {
      this.setAttribute(attributes.TRANSLATION_PARAMS, value);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.#translateAsync();
    } else {
      this.removeAttribute(attributes.TRANSLATION_PARAMS);
    }
  }

  /**
   * Get the current translation parameters
   * @returns {string | null} The translation parameters
   */
  get translationParams(): string | null {
    return this.getAttribute(attributes.TRANSLATION_PARAMS);
  }

  /**
   * Set the label wrap behavior
   * @param {string | null} value The label wrap setting ('ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch')
   */
  set labelWrap(value: string | null) {
    const validValues = ['ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch'];

    if (value && validValues.includes(value)) {
      this.setAttribute(attributes.LABEL_WRAP, value);
    } else {
      this.removeAttribute(attributes.LABEL_WRAP);
    }
  }

  /**
   * Get the current label wrap setting
   * @returns {string | null} The label wrap value
   */
  get labelWrap(): string | null {
    return this.getAttribute(attributes.LABEL_WRAP);
  }

  /**
   * Set the label width to constrain text when used with label-wrap
   * @param {string | null} value The width value (e.g., '120px', '300px')
   */
  set labelWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_WIDTH, value);
      this.style.setProperty('--ids-label-width', value);
    } else {
      this.removeAttribute(attributes.LABEL_WIDTH);
      this.style.removeProperty('--ids-label-width');
    }
  }

  /**
   * Get the current label width
   * @returns {string | null} The label width value
   */
  get labelWidth(): string | null {
    return this.getAttribute(attributes.LABEL_WIDTH);
  }

  #refreshContent() {
    if (!this.maskBtn) return;
    const maskBtnLabel = this.getMaskBtnLabel();
    this.maskBtn.setAttribute(attributes.TEXT, this.maskType === 'icon-mask' ? '' : maskBtnLabel.label);
  }
}
